// Product cards (snippets/product-card.liquid): one set of delegated listeners for every card on the page, cards
// added later by <lazy-section> or the theme editor included. Hearts are handled by wishlist.js.
// Desktop (990px and up):
// - the second photo loads on the first mouse hover of a card (never on touch or on phones);
// - the quick-add panel shows on hover, and opens with "+" (keyboard: focus moves into it). Esc, a click outside,
//   focus leaving the card or the mouse leaving it close it;
// - a colour swatch switches the sizes, the photo (alt "<title> – <colour>") and the price (that colour's first
//   variant with stock); a size adds at once (DE.cart.add), then the panel closes and the header fly-out shows.
// Phone: "+" opens the quick-add sheet (assets/quick-add.js, loaded on first use).
const DE = (window.DE = window.DE || {});
const desktop = window.matchMedia('(min-width: 990px)');
const hoverDesktop = window.matchMedia('(hover: hover) and (min-width: 990px)');
const strings = () => DE.config?.strings || {};

const dataOf = (card) => {
  if (card.variantData === undefined) {
    try {
      card.variantData = JSON.parse(card.querySelector('[data-variant-data]')?.textContent || 'null');
    } catch {
      card.variantData = null;
    }
  }
  return card.variantData;
};

const panelOf = (card) => card.querySelector('[data-card-panel]');
const plusOf = (card) => card.querySelector('[data-card-plus]');

/* ---------- Hover photo ---------- */
document.addEventListener(
  'pointerover',
  (event) => {
    if (event.pointerType === 'touch' || !hoverDesktop.matches) return;
    const card = event.target.closest?.('[data-card]');
    if (!card || card.dataset.hoverReady) return;
    card.dataset.hoverReady = 'true';
    const img = card.querySelector('.card__img--hover');
    if (!img) return;
    const loaded = () => img.classList.add('is-loaded');
    img.addEventListener('load', loaded, { once: true });
    DE.hydrate(img);
    if (img.complete && img.naturalWidth) loaded();
  },
  { passive: true }
);

/* ---------- Quick-add panel (desktop) ---------- */
let openCard = null;

const focusFirst = (card) => {
  const panel = panelOf(card);
  const target = panel?.querySelector('input:checked, button:not([disabled]), a[href]');
  target?.focus({ preventScroll: true });
};

const openPanel = (card, { focus = false } = {}) => {
  if (openCard && openCard !== card) closePanel(openCard);
  card.classList.remove('is-dismissed');
  card.classList.add('is-open');
  plusOf(card)?.setAttribute('aria-expanded', 'true');
  openCard = card;
  if (focus) focusFirst(card);
};

function closePanel(card = openCard, { restoreFocus = false } = {}) {
  if (!card) return;
  const panel = panelOf(card);
  if (restoreFocus && panel?.contains(document.activeElement)) plusOf(card)?.focus({ preventScroll: true });
  card.classList.remove('is-open');
  plusOf(card)?.setAttribute('aria-expanded', 'false');
  if (openCard === card) openCard = null;
}

const sizesHTML = (data, colour) => {
  const sold = strings().sold_out || 'Sold out';
  if (data.sizeIndex === null) {
    const row = data.v.find((r) => r[3] && (data.colourIndex === null || r[2] === colour));
    return row
      ? `<button type="button" class="size-btn size-btn--wide" data-variant-id="${row[0]}">${DE.escape(strings().add_to_bag || 'Add to bag')}</button>`
      : '';
  }
  const sizes = data.sizes?.length ? data.sizes : DE.sortSizes([...new Set(data.v.map((r) => r[1]))]);
  return sizes
    .map((size) => {
      const row = data.v.find((r) => r[1] === size && (data.colourIndex === null || r[2] === colour));
      if (row && row[3]) return `<button type="button" class="size-btn" data-variant-id="${row[0]}">${DE.escape(size)}</button>`;
      return `<span class="size-btn size-btn--sold" title="${DE.escape(sold)}">${DE.escape(size)}<span class="visually-hidden">, ${DE.escape(sold.toLowerCase())}</span></span>`;
    })
    .join('');
};

const srcsetOf = (url) => [360, 540, 720, 900].map((w) => `${DE.imageUrl(url, w)} ${w}w`).join(', ');

// Shows the colour's first photo on the card (the featured colour gets its original photo and hover photo back).
const swapPhoto = (card, data, colourName) => {
  const img = card.querySelector('.card__img--main');
  const colour = data.colours?.find((c) => c.name === colourName);
  if (!img) return;
  if (img.dataset.originalSrc === undefined) {
    img.dataset.originalSrc = img.getAttribute('src') || '';
    img.dataset.originalSrcset = img.getAttribute('srcset') || '';
    img.dataset.originalAlt = img.alt;
  }
  if (colourName === data.colour || !colour?.image) {
    if (img.dataset.originalSrcset) img.srcset = img.dataset.originalSrcset;
    img.src = img.dataset.originalSrc;
    img.alt = img.dataset.originalAlt;
    card.classList.remove('is-other-colour');
    return;
  }
  img.srcset = srcsetOf(colour.image);
  img.src = colour.image;
  img.alt = `${data.title} – ${colourName}`;
  card.classList.add('is-other-colour');
};

// The card's price for the colour chosen in the panel, as on the product page: that colour's first variant with stock
// (else its first variant). The struck-through compare-at price and the "Sale −X%" tag show only while it is a
// discount (DE.saleOff, the rule of snippets/sale-percent.liquid).
const showColourPrice = (card, data, colour) => {
  const inColour = (r) => data.colourIndex === null || r[2] === colour;
  const row = data.v.find((r) => r[3] && inColour(r)) || data.v.find(inColour);
  if (!row) return;
  const price = row[5] !== undefined ? row[5] : data.price;
  const compare = row[6] !== undefined ? row[6] : data.compare_at;
  const off = DE.saleOff(price, compare);
  card.querySelectorAll('[data-card-price]').forEach((el) => (el.textContent = DE.formatMoney(price)));
  card.querySelectorAll('[data-card-compare]').forEach((el) => {
    el.hidden = !off;
    const amount = el.querySelector('[data-card-compare-price]');
    if (amount) amount.textContent = off ? DE.formatMoney(compare) : '';
  });
  card.querySelectorAll('[data-card-sale-tag]').forEach((el) => {
    el.hidden = !off;
    const number = el.querySelector('[data-card-sale-off]');
    if (number) number.textContent = off ? String(off) : '';
  });
};

const showError = (card, message) => {
  const error = card.querySelector('[data-card-error]');
  if (!error) return;
  error.hidden = !message;
  error.textContent = message || '';
};

const addFromPanel = async (card, button) => {
  if (card.hasAttribute('aria-busy')) return;
  card.setAttribute('aria-busy', 'true');
  showError(card, '');
  try {
    await DE.cart.add([{ id: button.dataset.variantId, quantity: 1 }], { source: 'card' });
    if (panelOf(card)?.contains(document.activeElement)) plusOf(card)?.focus({ preventScroll: true });
    closePanel(card);
    // Also hide the hover panel until the pointer leaves the card, as the design does.
    card.classList.add('is-dismissed');
  } catch (error) {
    openPanel(card);
    showError(card, error.message);
  } finally {
    card.removeAttribute('aria-busy');
  }
};

/* ---------- Quick-add sheet (phone) ---------- */
const openSheet = async (card, button) => {
  const data = dataOf(card);
  const fallback = () => {
    window.location.href = data?.url || card.querySelector('.card__link')?.href || '/';
  };
  if (!data) return fallback();
  button.setAttribute('aria-busy', 'true');
  try {
    if (!DE.quickAdd) await import(DE.config.assets.quick_add);
    const img = card.querySelector('.card__img--main');
    const shown = DE.quickAdd.open(data, { opener: button, image: img?.currentSrc || img?.src || '' });
    if (!shown) fallback();
  } catch {
    fallback();
  } finally {
    button.removeAttribute('aria-busy');
  }
};

/* ---------- Events ---------- */
document.addEventListener('click', (event) => {
  const plus = event.target.closest('[data-card-plus]');
  if (plus) {
    const card = plus.closest('[data-card]');
    if (!desktop.matches) {
      openSheet(card, plus);
    } else if (card.classList.contains('is-open')) {
      closePanel(card);
    } else {
      // event.detail is 0 for keyboard activation: then focus moves into the panel.
      openPanel(card, { focus: event.detail === 0 });
    }
    return;
  }

  const size = event.target.closest('[data-card-panel] [data-variant-id]');
  if (size) {
    addFromPanel(size.closest('[data-card]'), size);
    return;
  }

  if (openCard && !openCard.contains(event.target)) closePanel(openCard);
});

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-card-panel] [data-colour-input]');
  if (!input) return;
  const card = input.closest('[data-card]');
  const data = dataOf(card);
  if (!data) return;
  const name = card.querySelector('[data-colour-name]');
  if (name) name.textContent = input.value;
  const sizes = card.querySelector('[data-card-sizes]');
  if (sizes) sizes.innerHTML = sizesHTML(data, input.value);
  swapPhoto(card, data, input.value);
  showColourPrice(card, data, input.value);
  showError(card, '');
  openPanel(card);
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !openCard) return;
  event.preventDefault();
  closePanel(openCard, { restoreFocus: true });
});

document.addEventListener('focusout', (event) => {
  const card = openCard;
  if (!card || !card.contains(event.target)) return;
  if (!event.relatedTarget || !card.contains(event.relatedTarget)) closePanel(card);
});

// The mouse leaving a card closes its panel (and ends the "dismissed after adding" state).
document.addEventListener('pointerout', (event) => {
  if (event.pointerType === 'touch') return;
  const card = event.target.closest?.('[data-card]');
  if (!card || card.contains(event.relatedTarget)) return;
  card.classList.remove('is-dismissed');
  if (card === openCard && !panelOf(card)?.contains(document.activeElement)) closePanel(card);
});

desktop.addEventListener('change', () => closePanel());

/* ---------- Unique ids ---------- */
// The same product can appear in two sections (New in and Under $30): renumber a repeated panel id, its hint id and
// its colour radio group, so aria-controls, aria-labelledby and the radios stay paired within each card.
let idCount = 0;
const ensureIds = (scope = document) => {
  scope.querySelectorAll('[data-card]').forEach((card) => {
    const panel = panelOf(card);
    if (!panel?.id || document.querySelectorAll(`[id="${CSS.escape(panel.id)}"]`).length < 2) return;
    const id = `${panel.id}-${++idCount}`;
    const hint = card.querySelector(`[id="${CSS.escape(`${panel.id}-hint`)}"]`);
    if (hint) {
      hint.id = `${id}-hint`;
      card.querySelector('[data-card-sizes]')?.setAttribute('aria-labelledby', hint.id);
    }
    card.querySelectorAll('[data-colour-input]').forEach((input) => (input.name = `${input.name}-${idCount}`));
    panel.id = id;
    plusOf(card)?.setAttribute('aria-controls', id);
  });
};

['lazy-section:loaded', 'shopify:section:load'].forEach((type) =>
  document.addEventListener(type, (event) => ensureIds(event.target instanceof Element ? event.target : document))
);
ensureIds();
